import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch } from "react-redux";
import { MdLockOutline } from "react-icons/md";
import { oauthSigninUser } from "../../../../services/oauth.services";
import { persistSession } from "../../../../helpers/session";
import { apiErrorMessage } from "../../../../helpers/oauthHelpers";
import { OAUTH_LOGIN_ROLE } from "../../../../constants/oauth";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import InputField from "../../../../Components/Common/Form/InputField";
import { useThemeMode } from "../../../../theme/ThemeModeContext";
import { BtnLoader, CardHeader } from "../OAuthShell/OAuthShell";
import { useTranslation } from "react-i18next";

// Copied from LoginForm rather than exported from it, to keep that file's diff
// down to the one-line session refactor.
const EnvelopeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const LockIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 11c-1.104 0-2-.896-2-2V6c0-1.104.896-2 2-2s2 .896 2 2v3c0 1.104-.896 2-2 2zm3 0h1c1.104 0 2 .896 2 2v7c0 1.104-.896 2-2 2H8c-1.104 0-2-.896-2-2v-7c0-1.104.896-2 2-2h1"
    />
  </svg>
);

/**
 * Scenario 2 -- the dedicated, connection-branded sign-in.
 *
 * There is NO useNavigate, no <Link> and no <Navigate> in this file, and there
 * must never be: a same-tab navigation would leave /authorize and lose the
 * OAuth query string with no way back. The footer links open in a new tab.
 */
const OAuthLoginCard = ({ clientName, notice, onLoggedIn, onCancel }) => {
  const { t } = useTranslation("auth");
  const dispatch = useDispatch();
  const { resolvedTheme } = useThemeMode();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = t("validation.emailOrUsernameRequired");
    }
    if (!formData.password || formData.password.trim() === "") {
      newErrors.password = t("validation.passwordRequired");
    }
    if (!verified) {
      newErrors.recaptcha = t("validation.recaptchaRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!validateForm()) return;

    setLoading(true);
    // oauthSigninUser, NOT auth.services.signinUser: a wrong password returns
    // 401 and the shared instance's interceptor would clear localStorage and
    // hard-navigate to /Login, destroying the request on the first typo.
    const res = await oauthSigninUser({
      email: formData.email,
      password: formData.password,
      role: OAUTH_LOGIN_ROLE,
    });
    setLoading(false);

    // The reCAPTCHA token is one-shot; a retry needs a fresh one.
    recaptchaRef.current?.reset();
    setVerified(false);

    if (res.error) {
      const message = apiErrorMessage(res.error);
      setFormError(message);
      dispatch(showSnackBar({ text: message, severity: "error" }));
      return;
    }

    const result = res?.data?.result;
    if (!result?.token) {
      setFormError(res?.data?.message || t("oauth.signInFailed"));
      return;
    }

    // Identical to the normal login, so the user stays signed in afterwards.
    const user = persistSession(result, dispatch);
    onLoggedIn(user);
  };

  const onRecaptchaChange = (value) => {
    setVerified(!!value);
    if (errors.recaptcha && value) {
      setErrors((prev) => ({ ...prev, recaptcha: "" }));
    }
  };

  return (
    <>
      <CardHeader
        icon={<MdLockOutline size={22} aria-hidden="true" />}
        title={t("oauth.signInTitle", { client: clientName })}
        subtitle={t("oauth.signInSubtitle")}
      />

      <form onSubmit={handleSubmit} className="space-y-3 p-5">
        {notice && (
          <p className="rounded-md bg-state-info-bg px-3 py-2 text-[12px] text-state-info-text">
            {notice}
          </p>
        )}
        {formError && (
          <p
            role="alert"
            className="rounded-md bg-state-danger-bg px-3 py-2 text-[12px] text-state-danger"
          >
            {formError}
          </p>
        )}

        {/* No role picker, no back arrow, no "Shipper Login" heading -- this is
            what makes it visibly not the normal login page. */}
        <p className="rounded-md bg-ui-surface-sunken px-3 py-2 text-xs text-ui-text-muted">
          {t("oauth.requesting", { client: clientName })}
        </p>

        <InputField
          label={t("login.usernameLabel")}
          type="text"
          value={formData.email}
          ref={emailRef}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          error={errors.email}
          placeholder={t("login.usernamePlaceholder")}
          icon={<EnvelopeIcon />}
          required
        />
        <InputField
          label={t("login.passwordLabel")}
          type="password"
          value={formData.password}
          ref={passwordRef}
          onChange={(e) => handleFieldChange("password", e.target.value)}
          error={errors.password}
          placeholder={t("login.passwordPlaceholder")}
          icon={<LockIcon />}
          required
        />

        <div className="flex justify-center py-1">
          <div className="origin-center scale-90 transform">
            <ReCAPTCHA
              sitekey="6Ld9BMcjAAAAAE-VKFPaecDHGEqeSKQdQ_GsZ-0W"
              onChange={onRecaptchaChange}
              ref={recaptchaRef}
              size="normal"
              theme={resolvedTheme === "dark" ? "dark" : "light"}
            />
          </div>
        </div>
        {errors.recaptcha && (
          <p className="text-center text-xs text-state-danger">
            {errors.recaptcha}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="h-11 w-full rounded-md bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? <BtnLoader /> : t("oauth.signInContinue")}
        </button>

        <p className="text-center text-[11px] text-ui-text-subtle">
          {t("oauth.staySignedIn", { client: clientName })}
        </p>

        <div className="space-y-1 border-t border-ui-border pt-3 text-center">
          {/* Plain anchors in a new tab, never react-router Links: navigating
              away in this tab would lose the OAuth request. */}
          <a
            href="/get-code"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm font-medium text-brand hover:text-brand-hover"
          >
            {t("login.forgotPassword")}
          </a>
          <a
            href="/register/shipper"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm font-medium text-brand hover:text-brand-hover"
          >
            {t("oauth.createShipper")}
          </a>
          <button
            type="button"
            onClick={onCancel}
            className="text-[11px] text-ui-text-muted hover:text-ui-text"
          >
            {t("oauth.cancelReturn", { client: clientName })}
          </button>
        </div>
      </form>
    </>
  );
};

export default OAuthLoginCard;
